import { Box, Button, Flex, Heading, Image, List, Stack, Text } from '@chakra-ui/react';
import { Link } from '@tanstack/react-router';

export interface ErrorPageProps {
  code: string;
  title: string;
  description: string;
  hints?: { id: string; icon: React.ReactNode; text: string }[];
  image: string;
  cta?: { label: string; to: string };
}

export function ErrorLayout({ code, title, description, hints = [], image, cta }: Readonly<ErrorPageProps>) {
  return (
    <Flex minH="100vh" align="center" justify="center" bg="white" px={4}>
      <Box alignSelf="center" mb={4} pos="absolute" top={4} left={4}>
        <Link to="/">
          <Image src="/assets/images/pilotrse-logo.svg" alt="Logo" height="60px" />
        </Link>
      </Box>

      <Stack
        direction={{ base: 'column', lg: 'row' }}
        gap={{ base: 10, md: 20 }}
        align="center"
        textAlign={{ base: 'center', md: 'left' }}
        maxW="6xl"
        w="full"
        py={10}
        px={{ base: 4, md: 8 }}
      >
        {/* Illustration */}
        <Box w={{ base: '80%', md: '50%' }} mx="auto">
          <Image src={image} alt={`Erreur ${code}`} objectFit="contain" w="full" />
        </Box>

        {/* Texte */}
        <Box maxW="480px" w="full">
          <Heading as="h1" size="2xl" mb={4} color="teal.700">
            {code} – {title}
          </Heading>

          <Heading as="h2" fontSize="lg" mb={6}>
            {description}
          </Heading>

          {hints.length > 0 && (
            <>
              <Text fontWeight="semibold" mb={3}>
                Plusieurs solutions sont possibles :
              </Text>

              <List.Root gap={3} mb={6} variant="plain" ml={6} textAlign="left">
                {hints.map((h) => (
                  <List.Item key={h.id}>
                    <List.Indicator asChild color="teal.500">
                      {h.icon}
                    </List.Indicator>
                    {h.text}
                  </List.Item>
                ))}
              </List.Root>
            </>
          )}

          {cta && (
            <Button asChild colorPalette="primary" size="lg" w={{ base: 'full', lg: 'auto' }}>
              <Link to={cta.to}>{cta.label}</Link>
            </Button>
          )}
        </Box>
      </Stack>
    </Flex>
  );
}
